import {
  type FieldType,
  type FieldValue,
  type FormField,
  fieldValidators,
  SMILEY_SCALE_VALUES
} from '@elfsight/widget-form-builder/widget';
import { format, isValid, parse } from 'date-fns';
import type {
  AllowedValueType,
  FieldOption,
  FieldStringType,
  FieldValidateExcludedType
} from '../types';
import { findInOptions } from './findInOptions';
import { normalizeValue } from './normalizeValue';
import { validateAllowedValue } from './validateAllowedValue';

const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';

type ValueValidator = (
  value: AllowedValueType,
  field: FormField
) => [FieldValue, boolean] | undefined;

function isValidatorKey(
  key: string | symbol
): key is keyof typeof fieldValidators {
  return key in fieldValidators;
}

const validateStringValue: ValueValidator = (value, field) => {
  if (!validateAllowedValue(value, ['string', 'number'])) {
    return;
  }

  const { type: fieldType } = field;
  const stringValue = value.toString();

  const validator = isValidatorKey(fieldType) && fieldValidators[fieldType];
  const isValid = !validator || validator(stringValue);

  return [stringValue, isValid];
};

const validateNumberValue: ValueValidator = (value, _field) => {
  if (validateAllowedValue(value, ['Date'])) {
    return;
  }

  const numberValue = Number(value);

  if (Array.isArray(value) || Number.isNaN(numberValue)) {
    return;
  }

  const { number: validator } = fieldValidators;
  const isValid = validator(numberValue);

  const stringValue = String(value);

  return [stringValue, isValid];
};

const validateTimeValue: ValueValidator = (value, _field) => {
  if (!validateAllowedValue(value, ['string'])) {
    return;
  }

  return [value, true];
};

const validateDateValue: ValueValidator = (value, field) => {
  if (!validateAllowedValue(value, ['string', 'Date'])) {
    return;
  }

  const { dateFormat } = field;
  const date =
    value instanceof Date
      ? value
      : parse(value, dateFormat ?? DEFAULT_DATE_FORMAT, new Date());

  if (isValid(date)) {
    const formattedDate = format(date, dateFormat ?? DEFAULT_DATE_FORMAT);

    return [formattedDate, true];
  }

  return;
};

function validateOptionValue(
  value: string | number,
  options: FieldOption[]
): [string, boolean] | undefined {
  const { id } = findInOptions(value, options) ?? {};
  if (id) {
    return [id, true];
  }

  return;
}

const validateChoiceValue: ValueValidator = (value, field) => {
  const { multiple, options } = field;
  const isSingleChoice = !multiple && Array.isArray(value);
  if (validateAllowedValue(value, ['Date']) || isSingleChoice || !options) {
    return;
  }

  if (Array.isArray(value)) {
    const validatedValue = value.reduce<string[]>((acc, valueElement) => {
      const [id] = validateOptionValue(valueElement, options) ?? [];
      if (id) {
        acc.push(id);
      }

      return acc;
    }, []);

    return [validatedValue, true];
  }

  return validateOptionValue(value, options);
};

const validateDropdownValue: ValueValidator = (value, field) => {
  const { options } = field;
  if (!options || !validateAllowedValue(value, ['string'])) {
    return;
  }

  return validateOptionValue(value, options);
};

const validateConsentValue: ValueValidator = (value, _field) => {
  if (!validateAllowedValue(value, ['string', 'number'])) {
    return;
  }

  const normalizedValue = normalizeValue(value);
  if (normalizedValue === 'yes' || value === 1) {
    return ['Yes', true];
  }

  if (normalizedValue === 'no' || value === 0) {
    return ['No', true];
  }

  return;
};

const validateNumberScaleValue: ValueValidator = (value, field) => {
  if (!validateAllowedValue(value, ['number', 'string'])) {
    return;
  }

  const numberValue = Number(value);
  const { range } = field;
  const [min, max] = range ?? [];
  if (!isNaN(numberValue) && numberValue >= min && numberValue <= max) {
    return [value, true];
  }

  return;
};

const validateStarScaleValue: ValueValidator = (value, field) => {
  if (!validateAllowedValue(value, ['number', 'string'])) {
    return;
  }

  const { numStars = 1 } = field;
  const numberValue = Number(value);
  if (!isNaN(numberValue) && numberValue >= 1 && numberValue <= numStars) {
    return [value, true];
  }

  return;
};

const validateThumbScaleValue: ValueValidator = (value, field) => {
  if (!validateAllowedValue(value, ['string'])) {
    return;
  }

  const normalizedValue = normalizeValue(value);
  const { likeText, dislikeText } = field;
  const isLike = likeText
    ? normalizedValue === normalizeValue(likeText)
    : normalizedValue === 'like';
  if (isLike) {
    return [likeText || 'Like', true];
  }

  const isDislike = dislikeText
    ? normalizedValue === normalizeValue(dislikeText)
    : normalizedValue === 'dislike';
  if (isDislike) {
    return [dislikeText || 'Dislike', true];
  }

  return;
};

const validateSmileyScaleValue: ValueValidator = (value, _field) => {
  if (!validateAllowedValue(value, ['string'])) {
    return;
  }

  const derivedValue = SMILEY_SCALE_VALUES.find(
    (option) => normalizeValue(option) === normalizeValue(value)
  );

  if (derivedValue) {
    return [derivedValue, true];
  }

  return;
};

type SubmissionValidatorsKey =
  | Exclude<FieldType, FieldStringType | FieldValidateExcludedType>
  | 'stringType';

export const submissionValidators: Record<
  SubmissionValidatorsKey,
  ValueValidator
> = {
  stringType: validateStringValue,
  number: validateNumberValue,
  time: validateTimeValue,
  date: validateDateValue,
  choice: validateChoiceValue,
  imageChoice: validateChoiceValue,
  dropdown: validateDropdownValue,
  consent: validateConsentValue,
  numberScale: validateNumberScaleValue,
  starScale: validateStarScaleValue,
  thumbScale: validateThumbScaleValue,
  smileyScale: validateSmileyScaleValue
};

export function isSubmissionValidatorKey(
  key: string | symbol
): key is SubmissionValidatorsKey {
  return key in submissionValidators;
}
